import {
  type ColumnFiltersState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type Table,
  type TableOptions,
  type Updater,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import React from 'react';

// Define the external state structure
type ExternalState<T> = {
  state: T;
  setState: (value: T) => void;
};

// Define the hook props
type UseDataTableProps<TData> = {
  onRowClick?: (row: TData) => void;
  externalState?: {
    sorting?: ExternalState<SortingState>;
    globalFilter?: ExternalState<string>;
    pagination?: ExternalState<PaginationState>;
    filters?: ExternalState<ColumnFiltersState>;
    visibility?: ExternalState<VisibilityState>;
    rowSelection?: ExternalState<RowSelectionState>;
  };
} & Omit<
  TableOptions<TData>,
  | 'pageCount'
  | 'getCoreRowModel'
  | 'manualFiltering'
  | 'manualPagination'
  | 'manualSorting'
  | 'state'
>;

export function useDataTable<TData>({
  externalState,
  onRowClick,
  ...props
}: UseDataTableProps<TData>): Table<TData> & {
  handleRowClick: (row: TData) => void;
  isRowClickable: boolean;
} {
  // Helper function to handle Updater logic
  const handleUpdater = <T,>(
    currentState: T,
    setState: (value: T) => void,
    updater: Updater<T>,
  ) => {
    if (typeof updater === 'function') {
      setState((updater as (old: T) => T)(currentState));
    } else {
      setState(updater);
    }
  };

  // Local state
  const [localSortingState, setLocalSortingState] =
    React.useState<SortingState>(props.initialState?.sorting ?? []);
  const [localGlobalFilterState, setLocalGlobalFilterState] =
    React.useState<string>('');
  const [localPaginationState, setLocalPaginationState] =
    React.useState<PaginationState>({
      pageIndex: 0,
      pageSize: props.initialState?.pagination?.pageSize ?? 10,
    });
  const [localFiltersState, setLocalFiltersState] =
    React.useState<ColumnFiltersState>(props.initialState?.columnFilters ?? []);
  const [localVisibilityState, setLocalVisibilityState] =
    React.useState<VisibilityState>(props.initialState?.columnVisibility ?? {});
  const [localRowSelectionState, setLocalRowSelectionState] =
    React.useState<RowSelectionState>(props.initialState?.rowSelection ?? {});

  // Determine state to use
  const sortingState = externalState?.sorting?.state ?? localSortingState;
  const setSortingState = externalState?.sorting?.setState
    ? (updater: Updater<SortingState>) => {
        if (externalState?.sorting) {
          handleUpdater(sortingState, externalState.sorting.setState, updater);
        }
      }
    : setLocalSortingState;

  const globalFilterState =
    externalState?.globalFilter?.state ?? localGlobalFilterState;
  const setGlobalFilterState = externalState?.globalFilter?.setState
    ? (updater: Updater<string>) => {
        if (externalState?.globalFilter?.setState) {
          handleUpdater(
            globalFilterState,
            externalState.globalFilter.setState,
            updater,
          );
        }
      }
    : setLocalGlobalFilterState;

  const paginationState =
    externalState?.pagination?.state ?? localPaginationState;
  const setPaginationState = externalState?.pagination?.setState
    ? (updater: Updater<PaginationState>) => {
        if (externalState?.pagination?.setState) {
          handleUpdater(
            paginationState,
            externalState.pagination.setState,
            updater,
          );
        } else {
          setLocalPaginationState(updater);
        }
      }
    : setLocalPaginationState;

  const filtersState = externalState?.filters?.state ?? localFiltersState;
  const setFiltersState = externalState?.filters?.setState
    ? (updater: Updater<ColumnFiltersState>) => {
        if (externalState?.filters) {
          handleUpdater(filtersState, externalState.filters.setState, updater);
        }
      }
    : setLocalFiltersState;

  const visibilityState =
    externalState?.visibility?.state ?? localVisibilityState;
  const setVisibilityState = externalState?.visibility?.setState
    ? (updater: Updater<VisibilityState>) => {
        if (externalState?.visibility?.setState) {
          handleUpdater(
            visibilityState,
            externalState.visibility.setState,
            updater,
          );
        }
      }
    : setLocalVisibilityState;

  const rowSelectionState =
    externalState?.rowSelection?.state ?? localRowSelectionState;
  const setRowSelectionState = externalState?.rowSelection?.setState
    ? (updater: Updater<RowSelectionState>) => {
        if (externalState?.rowSelection) {
          handleUpdater(
            rowSelectionState,
            externalState.rowSelection.setState,
            updater,
          );
        }
      }
    : setLocalRowSelectionState;

  // Create the table instance
  const table = useReactTable<TData>({
    ...props,
    defaultColumn: {
      size: 50,
      minSize: 20,
      maxSize: 500,
    },
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedRowModel: getFacetedRowModel(),
    state: {
      sorting: sortingState,
      pagination: paginationState,
      columnFilters: filtersState,
      columnVisibility: visibilityState,
      rowSelection: rowSelectionState,
      globalFilter: globalFilterState,
    },
    onGlobalFilterChange: setGlobalFilterState,
    onSortingChange: setSortingState,
    onPaginationChange: setPaginationState,
    onColumnFiltersChange: setFiltersState,
    onColumnVisibilityChange: setVisibilityState,
    onRowSelectionChange: setRowSelectionState,
  });

  const isRowClickable = Boolean(onRowClick);

  // Custom logic for row click handling
  const handleRowClick = (row: TData) => {
    if (isRowClickable && onRowClick) {
      onRowClick(row);
    }
  };

  return { ...table, handleRowClick, isRowClickable };
}
